import React, { useState, useEffect, useMemo } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { TextInput, Select, NumberInput } from '../ui/Input';
import { GRID_SIZE } from '../../constants/grid';

// Room types with associated colors
const ROOM_TYPES = {
  'Room': { color: 'rgba(100, 200, 255, 0.15)', label: 'Room (Generic)' },
  'Living Room': { color: 'rgba(180, 200, 150, 0.20)', label: 'Living Room' },
  'Bedroom': { color: 'rgba(150, 180, 220, 0.20)', label: 'Bedroom' },
  'Master Bedroom': { color: 'rgba(140, 170, 210, 0.20)', label: 'Master Bedroom' },
  'Kitchen': { color: 'rgba(255, 200, 100, 0.20)', label: 'Kitchen' },
  'Dining Room': { color: 'rgba(220, 180, 140, 0.20)', label: 'Dining Room' },
  'Bathroom': { color: 'rgba(100, 200, 220, 0.20)', label: 'Bathroom' },
  'Master Bath': { color: 'rgba(120, 210, 230, 0.20)', label: 'Master Bath' },
  'Half Bath': { color: 'rgba(140, 220, 240, 0.20)', label: 'Half Bath' },
  'Office': { color: 'rgba(200, 180, 160, 0.20)', label: 'Office' },
  'Den': { color: 'rgba(180, 160, 140, 0.20)', label: 'Den' },
  'Family Room': { color: 'rgba(190, 210, 160, 0.20)', label: 'Family Room' },
  'Laundry': { color: 'rgba(180, 220, 255, 0.20)', label: 'Laundry' },
  'Garage': { color: 'rgba(150, 150, 150, 0.20)', label: 'Garage' },
  'Closet': { color: 'rgba(220, 200, 180, 0.15)', label: 'Closet' },
  'Walk-in Closet': { color: 'rgba(210, 190, 170, 0.15)', label: 'Walk-in Closet' },
  'Pantry': { color: 'rgba(230, 210, 170, 0.15)', label: 'Pantry' },
  'Mudroom': { color: 'rgba(180, 170, 150, 0.20)', label: 'Mudroom' },
  'Foyer': { color: 'rgba(200, 190, 180, 0.15)', label: 'Foyer' },
  'Hallway': { color: 'rgba(190, 190, 190, 0.10)', label: 'Hallway' },
  'Utility': { color: 'rgba(160, 160, 170, 0.20)', label: 'Utility' },
  'Storage': { color: 'rgba(170, 160, 150, 0.15)', label: 'Storage' },
  'Basement': { color: 'rgba(140, 140, 150, 0.20)', label: 'Basement' },
  'Attic': { color: 'rgba(200, 190, 170, 0.15)', label: 'Attic' },
  'Porch': { color: 'rgba(180, 220, 180, 0.15)', label: 'Porch' },
  'Deck': { color: 'rgba(200, 180, 140, 0.15)', label: 'Deck' },
  'Patio': { color: 'rgba(190, 200, 160, 0.15)', label: 'Patio' },
};

// Generate options for Select component
const ROOM_TYPE_OPTIONS = Object.entries(ROOM_TYPES).map(([value, { label }]) => ({
  value,
  label,
}));

// Pixels per foot (GRID_SIZE = 20 pixels = 6 inches = 0.5 feet)
const PIXELS_PER_FOOT = GRID_SIZE * 2;

/**
 * Calculate area of a polygon using the Shoelace formula
 */
function calculatePolygonArea(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area / 2);
}

/**
 * Get bounding box of points
 */
function getBoundingBox(points) {
  if (!points || points.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  const minX = Math.min(...points.map(p => p.x));
  const maxX = Math.max(...points.map(p => p.x));
  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/**
 * RoomEditor - Editor panel for room properties
 */
export const RoomEditor = ({
  room,
  onUpdate,
  onDelete,
  onClose,
  onVertexSelect,
  x = 20,
  y = 100,
  isMobile = false,
}) => {
  const [minimized, setMinimized] = useState(isMobile); // Start minimized on mobile
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [customName, setCustomName] = useState(room?.name || 'Room');
  const [widthFeet, setWidthFeet] = useState(0);
  const [heightFeet, setHeightFeet] = useState(0);

  // Calculate room dimensions and area
  const roomMetrics = useMemo(() => {
    if (!room?.points || room.points.length < 3) {
      return { width: 0, height: 0, area: 0 };
    }
    const bbox = getBoundingBox(room.points);
    const areaPixels = calculatePolygonArea(room.points);
    return {
      width: bbox.width / PIXELS_PER_FOOT,
      height: bbox.height / PIXELS_PER_FOOT,
      area: areaPixels / (PIXELS_PER_FOOT * PIXELS_PER_FOOT),
      bbox,
    };
  }, [room?.points]);

  // Update local dimension state when room changes
  useEffect(() => {
    setWidthFeet(parseFloat(roomMetrics.width.toFixed(2)));
    setHeightFeet(parseFloat(roomMetrics.height.toFixed(2)));
  }, [roomMetrics.width, roomMetrics.height]);

  // Update custom name when room changes
  useEffect(() => {
    setCustomName(room?.name || 'Room');
  }, [room?.id, room?.name]);

  // Notify parent when vertex selection changes (for canvas highlighting)
  useEffect(() => {
    onVertexSelect?.(selectedVertexIndex);
    // Clear selection when component unmounts
    return () => onVertexSelect?.(null);
  }, [selectedVertexIndex, onVertexSelect]);

  if (!room) return null;

  const pointCount = room.points?.length || 0;

  // Delete a vertex by index
  const deleteVertex = (index) => {
    if (pointCount <= 3) return; // Need at least 3 points for a polygon
    const newPoints = [...room.points];
    newPoints.splice(index, 1);
    onUpdate({ points: newPoints });
    setSelectedVertexIndex(null);
  };

  // Add a vertex at the midpoint between two vertices
  const addVertexAfter = (index) => {
    const newPoints = [...room.points];
    const nextIndex = (index + 1) % pointCount;
    const p1 = newPoints[index];
    const p2 = newPoints[nextIndex];
    const midpoint = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
    newPoints.splice(index + 1, 0, midpoint);
    onUpdate({ points: newPoints });
  };

  // Handle room type selection - sets both name and color
  const handleRoomTypeSelect = (roomType) => {
    if (!roomType) return;
    const typeInfo = ROOM_TYPES[roomType];
    if (typeInfo) {
      setCustomName(roomType);
      onUpdate({ name: roomType, color: typeInfo.color, roomType });
    }
  };

  // Handle custom name input
  const handleNameChange = (name) => {
    setCustomName(name);
    onUpdate({ name });
  };

  // Handle dimension change - scale the room
  const handleDimensionChange = (dimension, newValue) => {
    if (!room?.points || room.points.length < 3 || !roomMetrics.bbox) return;

    const { bbox } = roomMetrics;
    const currentWidth = bbox.width;
    const currentHeight = bbox.height;

    // Calculate scale factors
    let scaleX = 1;
    let scaleY = 1;

    if (dimension === 'width' && currentWidth > 0) {
      const newWidthPixels = newValue * PIXELS_PER_FOOT;
      scaleX = newWidthPixels / currentWidth;
    } else if (dimension === 'height' && currentHeight > 0) {
      const newHeightPixels = newValue * PIXELS_PER_FOOT;
      scaleY = newHeightPixels / currentHeight;
    }

    // Scale all points from the center of the bounding box
    const centerX = (bbox.minX + bbox.maxX) / 2;
    const centerY = (bbox.minY + bbox.maxY) / 2;

    const newPoints = room.points.map(p => ({
      x: centerX + (p.x - centerX) * scaleX,
      y: centerY + (p.y - centerY) * scaleY,
    }));

    onUpdate({ points: newPoints });
  };

  // Determine current room type from name
  const currentRoomType = ROOM_TYPES[room.name] ? room.name : (room.roomType || '');

  return (
    <FloatingPanel
      title="Room Properties"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={isMobile ? 400 : 480}
    >
      <div style={{ padding: isMobile ? '8px' : '12px' }}>
        <PanelSection title="Room Type">
          <Select
            value={currentRoomType}
            onChange={handleRoomTypeSelect}
            options={[{ value: '', label: '-- Select Type --' }, ...ROOM_TYPE_OPTIONS]}
          />
          <TextInput
            value={customName}
            onChange={handleNameChange}
            placeholder="Custom name..."
            style={{ marginTop: '4px' }}
          />
        </PanelSection>

        <PanelSection title="Dimensions">
          <NumberInput
            label="Width"
            value={widthFeet}
            onChange={(val) => {
              setWidthFeet(val);
              handleDimensionChange('width', val);
            }}
            min={1}
            step={0.5}
            suffix="ft"
          />
          <NumberInput
            label="Height"
            value={heightFeet}
            onChange={(val) => {
              setHeightFeet(val);
              handleDimensionChange('height', val);
            }}
            min={1}
            step={0.5}
            suffix="ft"
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isMobile ? '8px 10px' : '10px 12px',
            background: 'rgba(0,200,255,0.1)',
            borderRadius: '6px',
            border: '1px solid rgba(0,200,255,0.2)',
          }}>
            <span style={{ color: '#6080a0', fontSize: isMobile ? '10px' : '11px' }}>Square Footage</span>
            <span style={{ color: '#00c8ff', fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
              {roomMetrics.area.toFixed(1)} sq ft
            </span>
          </div>
        </PanelSection>

        {/* Hide vertices section on mobile - less useful for touch interaction */}
        {!isMobile && (
          <PanelSection title="Vertices">
            <div style={{ color: '#6080a0', fontSize: '11px', marginBottom: '8px' }}>
              {pointCount} vertices - click to select, then use buttons below
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px',
              marginBottom: '8px',
              maxHeight: '80px',
              overflowY: 'auto'
            }}>
              {room.points?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedVertexIndex(selectedVertexIndex === index ? null : index)}
                  style={{
                    width: '28px',
                    height: '28px',
                    border: selectedVertexIndex === index ? '2px solid #00ffaa' : '1px solid #4080a0',
                    borderRadius: '4px',
                    background: selectedVertexIndex === index ? 'rgba(0,255,170,0.2)' : 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="default"
                onClick={() => selectedVertexIndex !== null && addVertexAfter(selectedVertexIndex)}
                disabled={selectedVertexIndex === null}
                style={{ flex: 1, fontSize: '11px', padding: '6px' }}
              >
                + Add After
              </Button>
              <Button
                variant="default"
                onClick={() => selectedVertexIndex !== null && deleteVertex(selectedVertexIndex)}
                disabled={selectedVertexIndex === null || pointCount <= 3}
                style={{ flex: 1, fontSize: '11px', padding: '6px' }}
              >
                - Delete
              </Button>
            </div>
          </PanelSection>
        )}

        <PanelSection title="Actions">
          <Button
            variant="danger"
            onClick={onDelete}
            style={{ width: '100%' }}
          >
            Delete Room
          </Button>
        </PanelSection>
      </div>
    </FloatingPanel>
  );
};

export default RoomEditor;
